import { useState, type ReactNode } from 'react';
import { Globe, FileText, ChevronDown, ChevronUp, Crosshair, TriangleAlert as AlertTriangle, DollarSign, Target, Swords } from 'lucide-react';
import { getLogoUrl } from '../../lib/logo';
import type { VendorIntelligence, VendorCompetitor, VerticalDifferentiator, VendorBattlecard } from '../../types';
import BattlecardView from './BattlecardView';
import FormattedText from '../common/FormattedText';

interface Props {
  competitor: VendorCompetitor;
  differentiators: VerticalDifferentiator[];
  battlecard: VendorBattlecard | null;
  vendorIntelligence: VendorIntelligence;
}

export default function CompetitorDetail({ competitor, differentiators, battlecard, vendorIntelligence }: Props) {
  const [activeSection, setActiveSection] = useState<'battlecard' | 'overview'>('battlecard');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);

  return (
    <div className="space-y-6">
      <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
        <div className="flex items-start gap-5">
          <img
            src={getLogoUrl(competitor.competitor_url)}
            alt=""
            className="w-16 h-16 rounded-lg object-contain bg-app-canvas p-2 flex-shrink-0"
            onError={e => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(competitor.competitor_name)}&background=e5e7eb&color=374151&size=64`; }}
          />
          <div className="flex-1">
            <h2 className="text-xl font-bold text-app-ink">{competitor.competitor_name}</h2>
            <div className="flex flex-wrap items-center gap-3 mt-1">
              {competitor.competitor_url && (
                <a href={competitor.competitor_url} target="_blank" rel="noopener noreferrer" className="text-xs text-forest-600 hover:underline flex items-center gap-1">
                  <Globe className="w-3 h-3" /> {competitor.competitor_url.replace(/^https?:\/\/(www\.)?/, '')}
                </a>
              )}
              {competitor.competitor_docs_url && (
                <a href={competitor.competitor_docs_url} target="_blank" rel="noopener noreferrer" className="text-xs text-app-muted hover:underline flex items-center gap-1">
                  <FileText className="w-3 h-3" /> Documentation
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-1 bg-app-canvas rounded-lg p-1">
        <button
          onClick={() => setActiveSection('battlecard')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition ${
            activeSection === 'battlecard' ? 'bg-app-surface text-forest-700 shadow-none' : 'text-app-muted hover:text-app-ink'
          }`}
        >
          <Swords className="w-4 h-4" /> Competitive Battlecard
        </button>
        <button
          onClick={() => setActiveSection('overview')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition ${
            activeSection === 'overview' ? 'bg-app-surface text-forest-700 shadow-none' : 'text-app-muted hover:text-app-ink'
          }`}
        >
          <Target className="w-4 h-4" /> Full Intelligence
        </button>
      </div>

      {activeSection === 'battlecard' && (
        battlecard ? (
          <BattlecardView
            battlecard={battlecard}
            differentiators={differentiators}
            competitorName={competitor.competitor_name}
          />
        ) : (
          <div className="text-center py-12 bg-app-surface rounded-lg border border-app-line">
            <Crosshair className="w-10 h-10 text-app-line-strong mx-auto mb-3" />
            <p className="text-sm text-app-muted">No competitive battlecard available for this competitor yet.</p>
            <p className="text-xs text-app-muted mt-1">Re-run the competitor research from Settings to generate a battlecard.</p>
          </div>
        )
      )}

      {activeSection === 'overview' && (
        <div className="space-y-5">
          {competitor.executive_overview && (
            <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
              <h3 className="text-base font-semibold text-app-ink mb-3">Executive Overview</h3>
              <FormattedText className="text-sm text-app-ink" text={competitor.executive_overview} />
            </div>
          )}

          {competitor.market_position?.category && (
            <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
              <h3 className="text-base font-semibold text-app-ink mb-3">Market Position</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {competitor.market_position.category && (
                  <InfoBlock label="Category" value={competitor.market_position.category} />
                )}
                {competitor.market_position.analyst_positioning && (
                  <InfoBlock label="Analyst Positioning" value={competitor.market_position.analyst_positioning} />
                )}
                {competitor.market_position.market_perception && (
                  <InfoBlock label="Market Perception" value={competitor.market_position.market_perception} />
                )}
                {competitor.market_position.strategic_ambition && (
                  <InfoBlock label="Strategic Ambition" value={competitor.market_position.strategic_ambition} />
                )}
              </div>
            </div>
          )}

          {(competitor.key_differentiators || []).length > 0 && (
            <CollapsibleCard
              title={`Key Differentiators (${(competitor.key_differentiators as unknown[]).length})`}
              isOpen={expandedSection === 'diffs'}
              onToggle={() => setExpandedSection(expandedSection === 'diffs' ? null : 'diffs')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(competitor.key_differentiators || []).map((d, i) => (
                  <div key={i} className="bg-app-canvas rounded-lg p-3.5">
                    <h5 className="text-sm font-semibold text-app-ink mb-1">{d.title}</h5>
                    <p className="text-xs text-app-muted leading-relaxed">{d.description}</p>
                  </div>
                ))}
              </div>
            </CollapsibleCard>
          )}

          {(competitor.core_problems || []).length > 0 && (
            <CollapsibleCard
              title={`Core Problems (${(competitor.core_problems as unknown[]).length})`}
              isOpen={expandedSection === 'problems'}
              onToggle={() => setExpandedSection(expandedSection === 'problems' ? null : 'problems')}
            >
              <div className="space-y-3">
                {(competitor.core_problems || []).map((p, i) => (
                  <div key={i} className="bg-app-canvas rounded-lg p-3.5">
                    <h5 className="text-sm font-semibold text-app-ink mb-1">{p.title}</h5>
                    <p className="text-xs text-app-muted">{p.problem_statement}</p>
                  </div>
                ))}
              </div>
            </CollapsibleCard>
          )}

          {competitor.technical_architecture && (
            <CollapsibleCard
              title="Technical Architecture"
              isOpen={expandedSection === 'tech'}
              onToggle={() => setExpandedSection(expandedSection === 'tech' ? null : 'tech')}
            >
              <FormattedText className="text-sm text-app-ink" text={competitor.technical_architecture} />
            </CollapsibleCard>
          )}

          {(competitor.industry_verticals || []).length > 0 && (
            <CollapsibleCard
              title={`Industry Verticals (${(competitor.industry_verticals as unknown[]).length})`}
              isOpen={expandedSection === 'verticals'}
              onToggle={() => setExpandedSection(expandedSection === 'verticals' ? null : 'verticals')}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(competitor.industry_verticals || []).map((v, i) => (
                  <div key={i} className="bg-app-canvas rounded-lg p-3.5">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-semibold text-app-ink">{v.name}</h5>
                      <div className="flex items-center gap-1.5">
                        <div className="w-12 h-1.5 bg-app-line rounded-full overflow-hidden" role="progressbar" aria-label={`${v.name} strength`} aria-valuemin={0} aria-valuemax={10} aria-valuenow={v.score || 0}>
                          <div
                            className={`h-full rounded-full ${
                              v.score >= 8 ? 'bg-app-positive' : v.score >= 5 ? 'bg-app-warning' : 'bg-app-critical'
                            }`}
                            style={{ width: `${(v.score || 0) * 10}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-app-muted">{v.score}/10</span>
                      </div>
                    </div>
                    <p className="text-xs text-app-muted line-clamp-2">{v.challenges}</p>
                  </div>
                ))}
              </div>
            </CollapsibleCard>
          )}

          {competitor.pricing_positioning && (
            <div className="bg-app-surface rounded-lg border border-app-line shadow-none p-6">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-5 h-5 text-app-muted" />
                <h3 className="text-base font-semibold text-app-ink">Pricing</h3>
              </div>
              <FormattedText className="text-sm text-app-ink" text={competitor.pricing_positioning} />
            </div>
          )}

          {competitor.risks_and_gaps && (
            <div className="bg-app-warning/10 rounded-lg border border-app-warning p-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-app-warning" />
                <h3 className="text-base font-semibold text-app-ink">Their Risks & Gaps</h3>
              </div>
              <FormattedText className="text-sm text-app-ink" text={competitor.risks_and_gaps} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-app-canvas rounded-lg p-3.5">
      <p className="text-xs font-medium text-app-muted mb-0.5">{label}</p>
      <p className="text-sm text-app-ink">{value}</p>
    </div>
  );
}

function CollapsibleCard({ title, isOpen, onToggle, children }: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  return (
    <div className="bg-app-surface rounded-lg border border-app-line shadow-none overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between p-5 hover:bg-app-canvas transition-colors">
        <h3 className="text-base font-semibold text-app-ink">{title}</h3>
        {isOpen ? <ChevronUp className="w-4 h-4 text-app-muted" /> : <ChevronDown className="w-4 h-4 text-app-muted" />}
      </button>
      {isOpen && <div className="px-5 pb-5">{children}</div>}
    </div>
  );
}
